import * as React from 'react';
import { Dialog, Transition } from '@headlessui/react';

import * as A from 'fp-ts/Array';
import * as O from 'fp-ts/Option';
import { pipe } from 'fp-ts/lib/function';

import { usePreventBodyScroll } from '../hooks/usePreventBodyScroll';
import { isNil } from '../utils/isNil';
import { useId } from '../hooks/useId';
import { LabelContent } from './LabelContent';
import { InputError } from './InputError';
import { FeatherIcon } from './FeatherIcon';
import { TextButton } from './ClickableText';

export interface ListSelection<T = string> {
  name: React.ReactNode;
  value: T;
  disabled?: boolean;
  title?: string;
  searchValue?: string;
}

interface Props<T extends string = string> {
  onChange: (v: T) => void;
  value: T;
  options: ListSelection<T>[];
  label: string;
  error?: string;
  showFilter?: boolean;
}

export const ListSelector = <T extends string>(props: Props<T>) => {
  const [selecting, setSelecting] = React.useState(false);
  const filterInputRef = React.useRef(null);
  const [filterQuery, setFilterQuery] = React.useState('');
  const id = useId();
  usePreventBodyScroll(selecting);
  const toggleSelecting = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setFilterQuery('');
    setSelecting((selecting) => !selecting);
  };

  const selectValue = (value: T) => (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    props.onChange(value);
    setFilterQuery('');
    setSelecting(false);
  };

  const selectedOption = pipe(
    props.options,
    A.findFirst((option) => option.value === props.value),
  );

  const filteredOptions = props.showFilter
    ? props.options.filter(({ searchValue }) => {
        return (
          isNil(searchValue) ||
          searchValue?.toLowerCase().includes(filterQuery.toLowerCase())
        );
      })
    : props.options;

  const filterInputId = `filter-input-${id}`;

  return (
    <>
      <div className="space-y-1">
        <label className="block" aria-invalid={props.error ? true : undefined}>
          <LabelContent>{props.label}</LabelContent>
          <button
            type="button"
            onClick={toggleSelecting}
            className={`block border ${
              props.error ? 'border-red' : 'border-grey-200'
            } rounded-lg w-full text-left p-4 bg-white hover:bg-grey-100 transition shadow-sm`}
          >
            {pipe(
              selectedOption,
              O.fold(
                () => '(Select)',
                ({ name }) => name,
              ),
            )}
          </button>
        </label>
        {props.error && <InputError>{props.error}</InputError>}
      </div>

      <Transition appear show={selecting} as={React.Fragment}>
        <Dialog
          onClose={() => setSelecting(false)}
          className="fixed inset-0 z-50 overflow-y-auto"
          initialFocus={filterInputRef}
        >
          <div className="flex items-center justify-center min-h-screen ">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-grey-900 bg-opacity-60 backdrop-filter backdrop-blur-sm pt-safe" />
            </Transition.Child>
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-100"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className="relative bg-white shadow-xl w-10/12 md:w-2/3 md:max-w-xl rounded-lg mx-auto my-10 md:my-16 overflow-hidden z-20">
                <div className="flex items-center">
                  <button
                    type="button"
                    onClick={toggleSelecting}
                    className="p-4 bg-transparent focus:outline-none focus:bg-grey-100"
                  >
                    <FeatherIcon icon="x" />
                  </button>
                  <div className="px-6 flex-1 text-center font-semibold text-lg">
                    {props.label}
                  </div>
                </div>
                {props.showFilter ? (
                  <div className="flex">
                    <label htmlFor={filterInputId} className="sr-only">
                      Search
                    </label>
                    <input
                      id={filterInputId}
                      ref={filterInputRef}
                      placeholder="Search..."
                      className="px-4 py-2 flex-1 outline-none"
                      onChange={(e) => setFilterQuery(e.target.value)}
                      value={filterQuery}
                    />
                    {filterQuery.length > 0 ? (
                      <TextButton
                        onClick={() => setFilterQuery('')}
                        className="px-4"
                      >
                        Clear
                      </TextButton>
                    ) : null}
                  </div>
                ) : null}

                {filteredOptions.map((option) => (
                  <button
                    type="button"
                    key={option.value}
                    onClick={
                      option.disabled ? undefined : selectValue(option.value)
                    }
                    className={`p-4 bg-transparent block w-full transition text-left border-t border-grey-200 ${
                      option.disabled
                        ? 'text-grey-400 cursor-not-allowed'
                        : 'hover:bg-grey-100 focus:outline-none focus:bg-grey-100'
                    }`}
                    disabled={option.disabled}
                    title={option.title}
                  >
                    <span>{option.name}</span>
                  </button>
                ))}
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};
